var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useLayoutEffect } from "react";
import clsx from "clsx";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useDialogContainer } from "core/Dialog";
import BodyText from "core/BodyText/CSSModule";
import styles from "./Tooltip.module.css";
// ─── Hooks ────────────────────────────────────────────────────────────────────
// If the trigger wrapper contains a natively focusable child (e.g. a button or
// link) or an already-labeled non-focusable indicator (e.g. an
// aria-current="page" span), remove the wrapper from the tab order to avoid a
// duplicate tab stop, and defer to that child's own role/label instead of the
// wrapper's. tabIndex=-1 keeps it programmatically focusable for Shift+Tab
// focus return.
var useAutoTabIndex = function (ref) {
    useLayoutEffect(function () {
        var el = ref.current;
        if (!el)
            return;
        var hasFocusableChild = !!el.querySelector('button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"]), [aria-current="page"]');
        if (hasFocusableChild) {
            el.tabIndex = -1;
            el.removeAttribute("role");
            el.removeAttribute("aria-label");
        }
    }, [ref]);
};
var parsePlacement = function (placement) {
    if (placement === void 0) { placement = "bottom"; }
    var parts = placement.split("-");
    var validSides = ["top", "bottom", "left", "right"];
    var side = validSides.includes(parts[0])
        ? parts[0]
        : "bottom";
    var align = parts[1] === "start" ? "start" : parts[1] === "end" ? "end" : "center";
    return { side: side, align: align };
};
var getFocusableElements = function (container) {
    return container
        ? Array.from(container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        : [];
};
// ─── Title ────────────────────────────────────────────────────────────────────
var Title = function (_a) {
    var _b = _a.textAlignment, textAlignment = _b === void 0 ? "left" : _b, _c = _a.darkMode, darkMode = _c === void 0 ? false : _c, isSmallTooltip = _a.isSmallTooltip, message = _a.message;
    return (_jsx(BodyText, { alignment: textAlignment, size: isSmallTooltip ? "sm" : "md", type: "div", className: clsx(styles.bodyText, darkMode && styles.bodyTextDarkMode), children: message }));
};
var ClickToOpen = function (_a) {
    var className = _a.className, children = _a.children, title = _a.title, _b = _a.placement, placement = _b === void 0 ? "bottom" : _b, _c = _a.darkMode, darkMode = _c === void 0 ? false : _c, _d = _a.isSmallTooltip, isSmallTooltip = _d === void 0 ? false : _d, _e = _a.interactive, interactive = _e === void 0 ? true : _e, zIndex = _a.zIndex, container = _a.container, ariaLabel = _a.ariaLabel;
    var _f = useState(false), isOpen = _f[0], setIsOpen = _f[1];
    var triggerRef = useRef(null);
    var contentRef = useRef(null);
    var _g = parsePlacement(placement), side = _g.side, align = _g.align;
    useAutoTabIndex(triggerRef);
    return (_jsxs(PopoverPrimitive.Root, { open: isOpen, onOpenChange: setIsOpen, children: [_jsx(PopoverPrimitive.Trigger, { asChild: true, children: _jsx("div", { ref: triggerRef, tabIndex: 0, role: "button", "aria-label": ariaLabel, "data-testid": "click-to-open", className: clsx(styles.childrenContainer, className), onClick: function (e) {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsOpen(function (v) { return !v; });
                    }, onKeyDown: function (e) {
                        // Only handle Enter when focused directly on this container, not a
                        // child element. If a child <button> is focused and Enter is pressed,
                        // the browser synthesizes a click that bubbles to our onClick handler,
                        // which toggles the popover — handling it here too would double-toggle.
                        if (e.key === "Enter" && e.target === e.currentTarget) {
                            setIsOpen(function (v) { return !v; });
                        }
                    }, children: children }) }), _jsx(PopoverPrimitive.Portal, { container: container, children: _jsxs(PopoverPrimitive.Content, { ref: contentRef, className: clsx(styles.tooltipContent, darkMode && styles.darkMode, isSmallTooltip && styles.small), style: zIndex ? { zIndex: zIndex } : undefined, side: side, align: align, sideOffset: 5, role: "tooltip", onOpenAutoFocus: !interactive ? function (e) { return e.preventDefault(); } : undefined, onCloseAutoFocus: !interactive ? function (e) { return e.preventDefault(); } : undefined, onFocusOutside: interactive ? function (e) { return e.preventDefault(); } : undefined, onKeyDown: interactive
                        ? function (e) {
                            var _a;
                            if (e.key === "Tab" && !e.shiftKey) {
                                var focusable = getFocusableElements(contentRef.current);
                                // Only redirect Tab when focus is on the container itself (or a
                                // focus guard), not when it's already on a focusable child.
                                // When onOpenAutoFocus lands on the container div (tabindex=-1)
                                // instead of a child — which happens when the popover is opened via
                                // keyboard because some browsers won't move focus during a keydown
                                // event — Tab would skip past the portal entirely. Redirecting here
                                // puts focus on the first focusable child instead.
                                // When focus IS already on a child (e.g. ChangeSize buttons), don't
                                // intercept: let Radix FocusScope handle Tab naturally so the user
                                // can move between multiple elements.
                                var isOnFocusableChild = focusable.includes(document.activeElement);
                                if (!isOnFocusableChild && focusable.length > 0) {
                                    e.preventDefault();
                                    focusable[0].focus();
                                }
                            }
                            // Shift+Tab from the first focusable element returns focus to the trigger.
                            if (e.key === "Tab" && e.shiftKey) {
                                var focusable = getFocusableElements(contentRef.current);
                                if (focusable.length > 0 &&
                                    document.activeElement === focusable[0]) {
                                    e.preventDefault();
                                    (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                }
                            }
                        }
                        : undefined, onClick: function () { return setIsOpen(false); }, children: [title, _jsx(PopoverPrimitive.Arrow, { className: clsx(styles.arrow, darkMode && styles.arrowDarkMode, isSmallTooltip && styles.arrowSmall) })] }) })] }));
};
var HoverToOpen = function (_a) {
    var className = _a.className, children = _a.children, open = _a.open, title = _a.title, _b = _a.placement, placement = _b === void 0 ? "bottom" : _b, _c = _a.leaveDelay, leaveDelay = _c === void 0 ? 100 : _c, _d = _a.darkMode, darkMode = _d === void 0 ? false : _d, _e = _a.isSmallTooltip, isSmallTooltip = _e === void 0 ? false : _e, zIndex = _a.zIndex, container = _a.container;
    var _f = useState(false), internalOpen = _f[0], setInternalOpen = _f[1];
    var closeTimerRef = useRef(null);
    var triggerRef = useRef(null);
    useAutoTabIndex(triggerRef);
    var isControlled = typeof open === "boolean";
    var _g = parsePlacement(placement), side = _g.side, align = _g.align;
    var handleOpenChange = function (newOpen) {
        if (isControlled)
            return;
        if (closeTimerRef.current)
            clearTimeout(closeTimerRef.current);
        if (newOpen) {
            setInternalOpen(true);
        }
        else {
            closeTimerRef.current = setTimeout(function () { return setInternalOpen(false); }, leaveDelay);
        }
    };
    var openProps = isControlled
        ? { open: open }
        : { open: internalOpen, onOpenChange: handleOpenChange };
    return (_jsx(TooltipPrimitive.Provider, { delayDuration: 0, children: _jsxs(TooltipPrimitive.Root, __assign({}, openProps, { children: [_jsx(TooltipPrimitive.Trigger, { asChild: true, children: _jsx("div", { ref: triggerRef, className: clsx(styles.childrenContainer, className), tabIndex: 0, children: children }) }), _jsx(TooltipPrimitive.Portal, { container: container, children: _jsxs(TooltipPrimitive.Content, { className: clsx(styles.tooltipContent, darkMode && styles.darkMode, isSmallTooltip && styles.small), style: zIndex ? { zIndex: zIndex } : undefined, side: side, align: align, sideOffset: 5, children: [title, _jsx(TooltipPrimitive.Arrow, { className: clsx(styles.arrow, darkMode && styles.arrowDarkMode, isSmallTooltip && styles.arrowSmall) })] }) })] })) }));
};
// ─── Tooltip ──────────────────────────────────────────────────────────────────
var Tooltip = function (_a) {
    var _b = _a.darkMode, darkMode = _b === void 0 ? false : _b, _c = _a.size, size = _c === void 0 ? "large" : _c, _d = _a.clickToOpen, clickToOpen = _d === void 0 ? false : _d, _e = _a.interactive, interactive = _e === void 0 ? true : _e, textAlignment = _a.textAlignment, message = _a.message, children = _a.children, className = _a.className, _f = _a.leaveDelay, leaveDelay = _f === void 0 ? 100 : _f, _g = _a.placement, placement = _g === void 0 ? "bottom" : _g, _h = _a.open, open = _h === void 0 ? null : _h, _j = _a.ariaLabel, ariaLabel = _j === void 0 ? "More information" : _j, zIndex = _a.zIndex, _slotProps = _a.slotProps, _PopperProps = _a.PopperProps;
    var dialogContainer = useDialogContainer();
    var isSmallTooltip = size === "small";
    var title = (_jsx(Title, { textAlignment: textAlignment, darkMode: darkMode, message: typeof message === "string" || React.isValidElement(message)
            ? message
            : String(message), isSmallTooltip: isSmallTooltip }));
    var sharedProps = {
        className: className,
        darkMode: darkMode,
        isSmallTooltip: isSmallTooltip,
        placement: placement,
        leaveDelay: leaveDelay,
        title: title,
        zIndex: zIndex,
        interactive: interactive,
        ariaLabel: ariaLabel,
        container: dialogContainer,
    };
    return (_jsx("div", { className: clsx(styles.container, className), children: clickToOpen ? (_jsx(ClickToOpen, __assign({}, sharedProps, { children: children }))) : (_jsx(HoverToOpen, __assign({}, sharedProps, { open: open, children: children }))) }));
};
export default Tooltip;
